/**
 * What the Delete, Detach and Revoke dialogs say about the items that use an account (spec "Delete,
 * Detach and Revoke confirmation"). Pure: the counts come from `settings.warehouses.removalImpact`.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

export type AccountRemovalCounts = Awaited<
  ReturnType<RouterClient<AppRouter>['settings']['warehouses']['removalImpact']>
>;

export type RemovalPath = 'delete' | 'detach' | 'revoke';

/**
 * What happens to the items after each path, before account repair (PR 1, and any workspace with the
 * repair flag off). Detach and Revoke never mention repair: nothing needs repair after them.
 */
export const REMOVAL_CONSEQUENCE: Record<RemovalPath, string> = {
  delete: 'They stop working until the account is added again.',
  detach: 'They stop working until this account is re-adopted or re-saved.',
  revoke: 'They stop working until you Reconnect.',
};

/** The consequence for a single item: "It stops working …". */
function singularConsequence(sentence: string): string {
  return sentence.replace(/^They stop /, 'It stops ');
}

/**
 * Delete with account repair on (ACT2-1286 PR 2, spec "Delete, Detach and Revoke confirmation"): the
 * items need repair and the workflows pause. The copy follows the flag because the behaviour does.
 */
function repairDeleteConsequence(counts: AccountRemovalCounts, single: boolean): string {
  if (single) {
    return counts.workflows === 1
      ? 'It stops working until someone repairs it, and it pauses.'
      : 'It stops working until someone repairs it.';
  }
  if (counts.workflows === 0) return 'They stop working until someone repairs them.';
  return counts.workflows === 1
    ? 'They stop working until someone repairs them, and the workflow pauses.'
    : 'They stop working until someone repairs them, and the workflows pause.';
}

export const NOTHING_USES_ACCOUNT = 'Nothing uses this account.';

function counted(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "a", "a and b", "a, b and c". */
function listJoin(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function syncSentence(counts: AccountRemovalCounts, path: RemovalPath): string | null {
  const synced = counts.audiencesSynced;
  if (synced === 0) return null;
  const activations = counts.audiencesWithActivations;
  const withActivations = activations > 0 ? ` (${activations} with activations)` : '';
  if (path === 'delete' && synced === 1 && counts.audiences === 1 && activations === 0) {
    return 'The audience keeps syncing in RudderStack.';
  }
  const subject = synced === 1 ? '1 audience is' : `${synced} audiences are`;
  if (path === 'delete') {
    return `${subject} synced to RudderStack${withActivations} and ${synced === 1 ? 'keeps' : 'keep'} syncing there.`;
  }
  return `${subject} synced to RudderStack${withActivations}.`;
}

/** The line above the bullets. */
export const REMOVAL_IMPACT_LEAD = 'What this affects:';

/** What the dialog says: a lead line, then one bullet per kind of item. No bullets when nothing uses it. */
export interface RemovalImpactLines {
  lead: string;
  bullets: string[];
}

/** "They stop working …" → "stop working …", so the bullet reads "4 dashboards stop working …". */
function predicate(sentence: string): string {
  return sentence.replace(/^(They|It) /, '');
}

/**
 * The dialog's lead line and bullets, in order. `repairEnabled` is the workspace's account-repair flag:
 * with it on, Delete says the items need repair and the workflows pause. Dashboards, audiences and
 * workflows lead; drafts, docs (and, for Detach and Revoke, repo links) follow on their own bullet. On
 * Delete, repo links get their own bullet, because their keys turn Disconnected. Zero types are left out.
 */
export function removalImpactLines(
  counts: AccountRemovalCounts,
  path: RemovalPath,
  options: { repairEnabled?: boolean } = {},
): RemovalImpactLines {
  const main = [
    counts.dashboards > 0 && counted(counts.dashboards, 'dashboard', 'dashboards'),
    counts.audiences > 0 && counted(counts.audiences, 'audience', 'audiences'),
    counts.workflows > 0 && counted(counts.workflows, 'workflow', 'workflows'),
  ].filter((p): p is string => typeof p === 'string');
  const also = [
    counts.audienceSketches > 0 && counted(counts.audienceSketches, 'audience draft', 'audience drafts'),
    counts.chatSketches > 0 && counted(counts.chatSketches, 'chat draft', 'chat drafts'),
    path !== 'delete' && counts.repoLinks > 0 && counted(counts.repoLinks, 'linked repository', 'linked repositories'),
    counts.docs > 0 && counted(counts.docs, 'generated doc', 'generated docs'),
  ].filter((p): p is string => typeof p === 'string');
  const repoLinksOnDelete = path === 'delete' && counts.repoLinks > 0;
  const hasSketches = counts.audienceSketches > 0 || counts.chatSketches > 0;

  if (main.length === 0 && also.length === 0 && !repoLinksOnDelete) return { lead: NOTHING_USES_ACCOUNT, bullets: [] };

  const bullets: string[] = [];
  // With no dashboard, audience or workflow, the drafts and docs carry the consequence.
  const lead = main.length > 0 ? main : also;
  if (lead.length > 0) {
    const single = lead.length === 1 && lead[0]!.startsWith('1 ');
    const consequence =
      // Docs alone have no Repair: they come back with the account, so they keep the PR 1 line.
      path === 'delete' && options.repairEnabled === true && (main.length > 0 || hasSketches)
        ? repairDeleteConsequence(counts, single)
        : single
          ? singularConsequence(REMOVAL_CONSEQUENCE[path])
          : REMOVAL_CONSEQUENCE[path];
    bullets.push(`${listJoin(lead)} ${predicate(consequence)}`);
  }
  const sync = syncSentence(counts, path);
  if (sync) bullets.push(sync);
  if (repoLinksOnDelete) {
    bullets.push(
      counts.repoLinks === 1
        ? '1 linked repository shows this key as Disconnected until an admin edits its account keys or this database is added again.'
        : `${counts.repoLinks} linked repositories show this key as Disconnected until an admin edits their account keys or this database is added again.`,
    );
  }
  if (main.length > 0 && also.length > 0) {
    const single = also.length === 1 && also[0]!.startsWith('1 ');
    bullets.push(`${listJoin(also)} also ${single ? 'uses' : 'use'} this account.`);
  }
  return { lead: REMOVAL_IMPACT_LEAD, bullets };
}
